import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { IconButton, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { Box } from '@mui/material';
import { Paper } from '@mui/material';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate } from 'lib/functions/utils';
import React, { useState } from 'react';

export interface BuroData {
  lastUpdate: Date;
  companySize: string;
  totalDebt: number;
  totalDocuments: number;
}

export interface BankruptcyData {
  role: string;
  process: string;
  date: string;
}

interface Debt {
  key: string;
  month: string;
  amount: number;
}

export interface DebtData {
  lastUpdate: Date;
  directaVigente: Debt;
  directaMorosa: Debt;
  directaVencida: Debt;
  directaComercial: Debt;
  leasingVigente: Debt;
  leasingMorosa: Debt;
  creditoDisponible: Debt;
}

interface OthersSummaryProps {
  buroData: BuroData | null;
  bankruptcyData: BankruptcyData[];
  debtData: DebtData | null;
}

interface LineProps {
  left: string;
  right: string;
}

const Line = (props: LineProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        {props.left}:
      </Typography>
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {props.right}
      </Typography>
    </Box>
  );
};

const OthersSummary = (props: OthersSummaryProps) => {
  const { buroData, bankruptcyData, debtData } = props;
  const [showDebt, setShowDebt] = useState(false);
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Deuda
              </Typography>
              {debtData != null && (
                <IconButton
                  onClick={() => {
                    setShowDebt(!showDebt);
                  }}
                >
                  {showDebt ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              )}
            </div>
            {debtData == null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No hay datos, actualiza en la pestaña &quot;Deuda&quot;.
              </Typography>
            ) : (
              <Box sx={{ backgroundColor: '#ffffff', borderRadius: 1, p: 2 }}>
                <Line
                  left="Última actualización"
                  right={formatDate(debtData.lastUpdate)}
                />
                <Line left="Mes" right={`${debtData.directaVigente.month}`} />
                <Line
                  left={`${debtData.directaVigente.key} (miles)`}
                  right={`$${debtData.directaVigente.amount.toLocaleString()}`}
                />
                {showDebt && (
                  <>
                    <Line
                      left={`${debtData.directaMorosa.key} (miles)`}
                      right={`$${debtData.directaMorosa.amount.toLocaleString()}`}
                    />
                    <Line
                      left={`${debtData.directaVencida.key} (miles)`}
                      right={`$${debtData.directaVencida.amount.toLocaleString()}`}
                    />
                    <Line
                      left={`${debtData.directaComercial.key} (miles)`}
                      right={`$${debtData.directaComercial.amount.toLocaleString()}`}
                    />
                    <Line
                      left={`${debtData.leasingVigente.key} (miles)`}
                      right={`$${debtData.leasingVigente.amount.toLocaleString()}`}
                    />
                    <Line
                      left={`${debtData.leasingMorosa.key} (miles)`}
                      right={`$${debtData.leasingMorosa.amount.toLocaleString()}`}
                    />
                    <Line
                      left={`${debtData.creditoDisponible.key} (miles)`}
                      right={`$${debtData.creditoDisponible.amount.toLocaleString()}`}
                    />
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e0e0e0',
            }}
          >
            <div>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Buró
              </Typography>
            </div>
            {buroData == null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No hay datos, actualiza en la pestaña &quot;Buró&quot;.
              </Typography>
            ) : (
              <Box sx={{ backgroundColor: '#ffffff', borderRadius: 1, p: 2 }}>
                <Line
                  left="Última actualización"
                  right={formatDate(buroData.lastUpdate)}
                />
                <Line
                  left="Total deuda"
                  right={`$${buroData.totalDebt.toLocaleString()}`}
                />
                <Line
                  left="Total documentos"
                  right={buroData.totalDocuments.toString()}
                />
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ mb: 3, color: '#2c3e50' }}
            >
              Boletín Concursal
            </Typography>
            {bankruptcyData.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No hay procedimientos.
              </Typography>
            ) : (
              <Box sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}>
                <EnhancedTable
                  dataHeaders={[
                    { key: 'role', label: 'Rol' },
                    { key: 'process', label: 'Procedimiento' },
                    { key: 'date', label: 'Publicación', type: 'date' },
                  ]}
                  data={bankruptcyData}
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OthersSummary;
